"use client";
import {
  AuthenticatedUser,
  ISchedule,
  IUserFull,
  Office
} from "@gettactic/api";
import { useDisclosure } from "@gettactic/helpers/src";
import {
  useAuthenticated,
  useResource,
  useWorkplaceRequestsAllowed
} from "@gettactic/hooks";
import React, { useRef } from "react";
import { ReserveModal } from "@/features/reserve/components/Reserve/ReserveModal";
import { api } from "@/lib/api/api";
import { WorkplaceRequestsRef } from "@/types/workplace-requests";
import { ReserveContext } from "./context";
import { getDuplicateReservationData } from "./getDuplicateReservationData";
import { RoomSelectionProvider } from "./RoomSelectionProvider";
import {
  MapReserveCardTimeSettings,
  ReserveModalDisclosureOptions,
  ReserveProviderProps,
  ReserveState
} from "./types";

export const ReserveProvider: React.FC<
  React.PropsWithChildren<ReserveProviderProps>
> = ({ children, withModal = true, isOnReservationModal = false }) => {
  const reserveModalDisclosure = useDisclosure<ReserveModalDisclosureOptions>();
  const wpRequestsRef = useRef<WorkplaceRequestsRef | null>(null);
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const { resource } = useResource(reserveModalDisclosure.data?.resource?.id, {
    enabled: reserveModalDisclosure.isOpen
  });

  const initialTime = useRef<MapReserveCardTimeSettings>(null);

  const saveInitialTimeSettings = (options?: ReserveModalDisclosureOptions) => {
    initialTime.current = {
      start: options?.start,
      time: options?.time,
      duration: options?.duration || 0,
      mapStart: options?.mapStart,
      mapEnd: options?.mapEnd
    };
  };

  const openReserveModal = (
    options?: ReserveModalDisclosureOptions,
    forcePassThrough = false,
    forceSaveInitialTime = false
  ) => {
    // Save initial time if:
    // 1. Modal is not already open (normal first open), OR
    // 2. forceSaveInitialTime is true (e.g., for duplicate)
    if (!reserveModalDisclosure.isOpen || forceSaveInitialTime) {
      saveInitialTimeSettings(options);
    }
    if (forcePassThrough) {
      // Pass to main provider
      return;
    }
    reserveModalDisclosure.open(options);
  };

  const isWorkplaceRequestsAllowed =
    useWorkplaceRequestsAllowed("workplace_requests");
  const value: ReserveState = {
    openReserveModal,
    openDuplicateModal: async (
      item: ISchedule & { office?: Office },
      user?: IUserFull | AuthenticatedUser
    ) => {
      if (item) {
        saveInitialTimeSettings({
          start: new Date(item.start),
          time: new Date(item.start),
          duration: Math.round(
            (new Date(item.end).getTime() - new Date(item.start).getTime()) /
              (1000 * 60)
          ),
          resourceType: item.resource.type
        });
      }
      const options = await getDuplicateReservationData({
        item,
        user,
        isWorkplaceRequestsAllowed,
        api,
        authenticatedUser: authenticatedUser
      });
      openReserveModal(options);
    },
    closeReserveModal: () => {
      reserveModalDisclosure.close(undefined);
      return initialTime.current;
    },
    submitWorkplaceRequests: () =>
      wpRequestsRef.current?.submitWorkplaceRequests({}) ||
      Promise.resolve({ success: true }),
    reserveData: reserveModalDisclosure.data,
    isReserveOpen: reserveModalDisclosure.isOpen,
    resource
  };

  return (
    <ReserveContext.Provider value={value}>
      <RoomSelectionProvider>
        {children}
        {withModal && (
          <ReserveModal
            reserveModal={reserveModalDisclosure.isOpen}
            resourceType={reserveModalDisclosure.data?.resourceType || "desk"}
            start={reserveModalDisclosure.data?.start || new Date()}
            onClose={reserveModalDisclosure.close}
            resource={resource}
            duration={reserveModalDisclosure.data?.duration}
            time={reserveModalDisclosure.data?.time}
            floorId={reserveModalDisclosure.data?.floorId}
            office={reserveModalDisclosure.data?.office}
            isEmbedded={reserveModalDisclosure.data?.isEmbedded || false}
            workplaceRequestItems={
              reserveModalDisclosure.data?.workplaceRequestItems
            }
            wpRequestsRef={wpRequestsRef}
            attenders={reserveModalDisclosure?.data?.attenders}
            title={reserveModalDisclosure?.data?.title}
            owner={reserveModalDisclosure?.data?.owner}
            onSuccess={reserveModalDisclosure.data?.onSuccess}
            isAdvancedRecurringOpenByDefault={
              reserveModalDisclosure.data?.isAdvancedRecurringOpenByDefault
            }
            recurring={reserveModalDisclosure.data?.recurring}
            description={reserveModalDisclosure.data?.description}
          />
        )}
      </RoomSelectionProvider>
    </ReserveContext.Provider>
  );
};
